import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/infrastructure/lib/utils";

const cardVariants = cva("", {
  variants: {
    variant: {
      default: "",
      compact: "py-0",
      stat: "p-3",
      flat: "border-0 bg-card shadow-none",
      elevated: "border-0 shadow-lg",
      muted: "bg-muted/30",
      dashed: "border-dashed",
      danger: "bg-destructive/5 py-0 ring-destructive/20",
      dangerCompact: "border-destructive py-0",
      warning: "border-warning-border bg-warning/5",
      info: "border-info-border bg-info/5",
      subtle: "border-primary/20 bg-primary/[0.02]",
      interactive:
        "cursor-pointer py-0 transition-colors hover:bg-muted/50",
      spacious: "py-8",
      interactiveBorder:
        "transition-all hover:border-primary/50",
    },
  },
  defaultVariants: { variant: "default" },
});

type CardVariant =
  | "default"
  | "compact"
  | "stat"
  | "flat"
  | "elevated"
  | "muted"
  | "dashed"
  | "danger"
  | "dangerCompact"
  | "warning"
  | "info"
  | "subtle"
  | "interactive"
  | "spacious"
  | "interactiveBorder";

function Card({
  className,
  size = "default",
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  size?: "default" | "sm";
  variant?: CardVariant;
}) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        "group/card flex flex-col gap-4 overflow-hidden rounded-none bg-card py-4 text-xs/relaxed text-card-foreground ring-1 ring-foreground/10 has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 data-[size=sm]:gap-2 data-[size=sm]:py-3 data-[size=sm]:has-data-[slot=card-footer]:pb-0 *:[img:first-child]:rounded-none *:[img:last-child]:rounded-none",
        cardVariants({ variant }),
        className
      )}
      {...props}
    />
  );
}

function CardHeader({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?:
    | "default"
    | "compact"
    | "bordered"
    | "flush"
    | "tight";
}) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-none px-4 group-data-[size=sm]/card:px-3 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-4 group-data-[size=sm]/card:[.border-b]:pb-3",
        {
          default: "",
          compact: "pb-3",
          bordered: "border-b",
          flush: "border-b pb-0",
          tight: "pb-2",
        }[variant],
        className
      )}
      {...props}
    />
  );
}

function CardTitle({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?:
    | "default"
    | "small"
    | "base"
    | "large"
    | "withIcon"
    | "withIconBase"
    | "destructive";
}) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "text-sm font-medium group-data-[size=sm]/card:text-sm",
        {
          default: "",
          small: "text-sm",
          base: "text-base",
          large: "text-xl",
          withIcon: "flex items-center gap-2",
          withIconBase: "flex items-center gap-2 text-base",
          destructive:
            "flex items-center gap-2 text-destructive",
        }[variant],
        className
      )}
      {...props}
    />
  );
}

function CardDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        "text-xs/relaxed text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}

function _CardAction({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function CardContent({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?:
    | "default"
    | "p0"
    | "p4"
    | "p6"
    | "p8"
    | "pt4"
    | "pt6"
    | "pt8"
    | "py6"
    | "py12"
    | "center12"
    | "space2"
    | "space3"
    | "space4"
    | "space6"
    | "borderP0"
    | "borderP4"
    | "divideP0"
    | "muted"
    | "mutedCenter12";
}) {
  return (
    <div
      data-slot="card-content"
      className={cn(
        "px-4 group-data-[size=sm]/card:px-3",
        {
          default: "",
          p0: "p-0",
          p4: "p-4",
          p6: "p-6",
          p8: "p-8",
          pt4: "pt-4",
          pt6: "pt-6",
          pt8: "pt-8",
          py6: "py-6",
          py12: "py-12",
          center12: "py-12 text-center",
          space2: "space-y-2",
          space3: "space-y-3",
          space4: "space-y-4",
          space6: "space-y-6",
          borderP0: "border-t p-0",
          borderP4: "space-y-4 border-t px-4 pt-4 pb-4",
          divideP0: "divide-y divide-border p-0",
          muted: "text-muted-foreground",
          mutedCenter12:
            "py-12 text-center text-muted-foreground",
        }[variant],
        className
      )}
      {...props}
    />
  );
}

function _CardFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center rounded-none border-t p-4 group-data-[size=sm]/card:p-3",
        className
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
};
